import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { dialogueVoices, type Gloss, type Locale } from '@diana/shared'
import type { LessonDetail, LessonFigure, VocabEntry } from '../../../lib/api.js'
import { Icon } from '../../../classroom/icons.js'
import { speak, speakable, stopSpeaking, usePolishVoice } from '../../../lib/speak.js'
import { titleFor } from '../CoursePage.js'
import { Blocks, Dialogue, GlossList } from './blocks.js'
import { FigureC } from './FigureC.js'
import { useBack, useCounter, usePlayer, usePrimary } from './context.js'
import type { DialogueBlock, Step } from './steps.js'
import { HOMEWORK_KEY, sectionLabelPl } from './steps.js'

/* ── cover ──────────────────────────────────────────────────────────── */

export function CoverStep({ lesson }: { lesson: LessonDetail }) {
  const { locale, t, next } = usePlayer()
  usePrimary({ label: `${t.start} →`, onClick: next })
  const own = titleFor(lesson, locale)
  const cover = lesson.figures.find((f) => f.n === 1 && f.mediaId)
  return (
    <div className="flex flex-col gap-7">
      {cover && <FigureC figure={cover} />}
      <div className="flex flex-col gap-1.5">
        <span className="text-class-label font-semibold text-class-ink-faint">
          {[t.lesson(lesson.number), lesson.durationLabel ? t.cover.minutes(lesson.durationLabel) : ''].filter(Boolean).join(' · ')}
        </span>
        <h1 lang="pl" className="cl-title m-0">
          {lesson.title}
        </h1>
        {own !== lesson.title && <p className="m-0 text-class-ink-soft">{own}</p>}
      </div>
      {lesson.goals.length > 0 && (
        <div className="flex flex-col gap-3">
          <p className="m-0 font-semibold">{t.cover.after}</p>
          <ul lang="pl" className="m-0 flex list-none flex-col p-0 text-class-ink-soft">
            {lesson.goals.map((g) => (
              <li key={g} className="border-t border-class-line py-2.5 last:border-b">
                {g.replace(/[*_]/g, '')}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

/* ── word cards ─────────────────────────────────────────────────────── */

function useWide(): boolean {
  const query = '(min-width: 821px)'
  const [wide, setWide] = useState(() => typeof window !== 'undefined' && window.matchMedia(query).matches)
  useEffect(() => {
    const m = window.matchMedia(query)
    const on = () => setWide(m.matches)
    m.addEventListener('change', on)
    return () => m.removeEventListener('change', on)
  }, [])
  return wide
}

function glossOf(v: { uk: string; ru: string }, locale: Locale): string {
  return locale === 'ru' ? v.ru || v.uk : v.uk || v.ru
}

export function WordsStep({ entries, group }: { entries: VocabEntry[]; group: 'slowo' | 'zwrot' }) {
  const { locale, t, next } = usePlayer()
  const wide = useWide()
  const per = wide ? 6 : 1
  const pages = Math.max(1, Math.ceil(entries.length / per))
  const [page, setPage] = useState(0)
  const [open, setOpen] = useState<string | null>(null)
  const voice = usePolishVoice()
  useEffect(() => setPage((p) => Math.min(p, pages - 1)), [pages])

  const shown = entries.slice(page * per, page * per + per)
  usePrimary({
    label: wide ? `${t.next} ›` : t.next,
    onClick: () => {
      setOpen(null)
      if (page < pages - 1) setPage(page + 1)
      else next()
    },
  })
  useBack(
    page > 0
      ? () => {
          setOpen(null)
          setPage((p) => Math.max(0, p - 1))
          return true
        }
      : null,
  )
  useCounter(`${Math.min(entries.length, page * per + shown.length)} / ${entries.length}`)

  const title = group === 'zwrot' ? t.words.phrases : t.words.words

  if (!wide) {
    const v = shown[0]
    if (!v) return null
    const flipped = open === v.id
    return (
      <div className="flex flex-col gap-5">
        <button
          type="button"
          onClick={() => setOpen(flipped ? null : v.id)}
          className="flex min-h-[420px] cursor-pointer flex-col items-center justify-center gap-3 rounded-class-lg border-0 bg-class-white px-6 py-8 text-center font-[inherit] text-inherit shadow-[inset_0_0_0_1px_var(--color-class-hairline)]"
        >
          <span lang="pl" className="font-class-display text-class-title leading-tight">
            {v.term}
          </span>
          {v.pos && <span className="text-class-label font-semibold text-class-ink-faint">{v.pos}</span>}
          <span className="text-class-ink-soft">{glossOf(v, locale)}</span>
          {flipped && v.example ? (
            <span lang="pl" className="mt-4 w-full border-t border-class-line pt-4 font-class-display text-class-subheading italic">
              {v.example.replace(/[*_]/g, '')}
            </span>
          ) : (
            v.example && <span className="mt-5 text-class-label font-semibold text-class-ink-faint">{t.words.tapForExample}</span>
          )}
        </button>
        {voice && (
          <button
            type="button"
            onClick={() => speak(flipped && v.example ? v.example : v.term)}
            className="cl-link inline-flex min-h-11 items-center gap-2 self-center"
          >
            <Icon name="speaker" size={20} />
            {t.words.listen}
          </button>
        )}
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-1">
      <h1 className="cl-heading m-0">{title}</h1>
      <p className="m-0 text-class-gloss text-class-ink-soft">{t.words.hint}</p>
      <div className="mt-8 grid grid-cols-3 gap-5">
        {shown.map((v) => {
          const flipped = open === v.id
          return (
            <div
              key={v.id}
              className={[
                'relative flex min-h-[180px] flex-col rounded-class-md bg-class-white p-6',
                flipped ? 'shadow-[inset_0_0_0_2px_var(--color-class-sky-700)]' : 'shadow-[inset_0_0_0_1px_var(--color-class-hairline)]',
              ].join(' ')}
            >
              <button
                type="button"
                onClick={() => setOpen(flipped ? null : v.id)}
                aria-expanded={flipped}
                className="flex flex-1 cursor-pointer flex-col items-start gap-1 border-0 bg-transparent p-0 pr-9 text-left font-[inherit] text-inherit"
              >
                <span lang="pl" className="font-class-display text-class-subheading leading-tight">
                  {v.term}
                </span>
                <span className="text-class-ink-soft">{glossOf(v, locale)}</span>
                {flipped && v.example && (
                  <span lang="pl" className="mt-3 w-full border-t border-class-line pt-3 font-class-display italic">
                    {v.example.replace(/[*_]/g, '')}
                  </span>
                )}
              </button>
              {voice && (
                <button
                  type="button"
                  aria-label={`${t.words.listen}: ${v.term}`}
                  onClick={() => speak(flipped && v.example ? v.example : v.term)}
                  className="absolute top-4 right-4 inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border-0 bg-class-field-soft text-class-sky-700"
                >
                  <Icon name="speaker" size={18} />
                </button>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

/* ── text: explanation, tables, a dialogue scene, a lecture part ─────── */

export function TextStep({ step, figures }: { step: Extract<Step, { kind: 'text' }>; figures: LessonFigure[] }) {
  const { locale, t, next } = usePlayer()
  usePrimary({ label: `${t.next} ›`, onClick: next })
  const dialog = step.eyebrow.match(/^dialog:(\d+):(\d+)$/)
  const eyebrow = dialog ? t.dialogue.label(Number(dialog[1]), Number(dialog[2])) : step.eyebrow
  return (
    <div className="flex flex-col">
      {eyebrow && <span className="text-class-label font-semibold text-class-ink-faint">{eyebrow}</span>}
      <h1 lang="pl" className="cl-heading m-0 mt-1.5 mb-6">
        {step.title}
      </h1>
      <Blocks blocks={step.blocks} glosses={step.glosses} locale={locale} t={t} />
      {figures.map((f) => (
        <FigureC key={f.n} figure={f} />
      ))}
    </div>
  )
}

/* ── listening ──────────────────────────────────────────────────────── */

const AVATAR = ['#c8412b', '#1f3a5f', '#e0a526', '#2e6b4f', '#7a8aa6']

export function ListenStep({
  step,
  audio,
}: {
  step: Extract<Step, { kind: 'listen' }>
  audio: LessonDetail['audio']
}) {
  const { locale, t, next } = usePlayer()
  const voice = usePolishVoice()
  const [slow, setSlow] = useState(false)
  const [showText, setShowText] = useState(!audio && !voice)
  const [playing, setPlaying] = useState(false)
  const [line, setLine] = useState(0)
  const [time, setTime] = useState({ at: 0, total: audio?.durationSec ?? 0 })
  const audioRef = useRef<HTMLAudioElement>(null)
  const token = useRef(0)

  usePrimary({ label: `${t.listen.toExercise} ›`, onClick: next })

  const lines = useMemo(
    () =>
      step.scenes
        .flatMap((s) => {
          // Voices are chosen per scene, the same way the server chose them when it recorded the lines.
          const voices = dialogueVoices(s.lines)
          return s.lines.map((l) => ({ ...l, text: speakable(l.text), voice: voices.get(l.speaker) }))
        })
        .filter((l) => l.text),
    [step.scenes],
  )
  const speakers = useMemo(() => Array.from(new Set(lines.map((l) => l.speaker).filter(Boolean))), [lines])

  useEffect(
    () => () => {
      token.current++
      stopSpeaking()
    },
    [],
  )
  useEffect(() => {
    if (audioRef.current) audioRef.current.playbackRate = slow ? 0.75 : 1
  }, [slow])

  const playTts = async (from: number) => {
    const mine = ++token.current
    setPlaying(true)
    for (let i = from; i < lines.length; i++) {
      if (token.current !== mine) return
      setLine(i)
      await speak(lines[i]!.text, { slow, voice: lines[i]!.voice })
    }
    if (token.current === mine) {
      setPlaying(false)
      setLine(0)
    }
  }

  const toggle = () => {
    if (audio) {
      const el = audioRef.current
      if (!el) return
      if (el.paused) void el.play()
      else el.pause()
      return
    }
    if (playing) {
      token.current++
      stopSpeaking()
      setPlaying(false)
    } else void playTts(line)
  }

  const fmt = (s: number) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`
  const progress = audio ? (time.total ? time.at / time.total : 0) : lines.length ? line / lines.length : 0
  const active = !audio && playing ? lines[line]?.speaker : null
  const canPlay = Boolean(audio) || voice

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-1.5">
        <span className="text-class-label font-semibold text-class-ink-faint">{step.part}</span>
        <h1 lang="pl" className="cl-heading m-0">
          {step.title}
        </h1>
      </div>

      {canPlay ? (
        <section className="flex flex-col gap-5 rounded-class-lg bg-class-field px-5 py-6 desk:px-8">
          {speakers.length > 0 && (
            <div className="flex flex-wrap justify-center gap-4">
              {speakers.map((sp, i) => (
                <span key={sp} className="flex w-20 flex-col items-center gap-1">
                  <span
                    style={{ background: AVATAR[i % AVATAR.length] }}
                    className={[
                      'inline-flex h-12 w-12 items-center justify-center rounded-full text-[16px] font-bold text-class-white transition',
                      active === sp ? 'ring-[3px] ring-class-gold ring-offset-2 ring-offset-class-field' : active ? 'opacity-60' : '',
                    ].join(' ')}
                  >
                    {sp.slice(0, 1)}
                  </span>
                  <span className="text-center text-class-label leading-tight font-semibold text-class-ink-soft">{sp}</span>
                </span>
              ))}
            </div>
          )}
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={toggle}
              aria-label={playing ? t.listen.pause : t.listen.play}
              className="inline-flex h-16 w-16 shrink-0 cursor-pointer items-center justify-center rounded-full border-0 bg-class-gold-100 text-class-gold-text shadow-[inset_0_0_0_1px_var(--color-class-gold-line)]"
            >
              <Icon name={playing ? 'pause' : 'play'} size={22} fill />
            </button>
            <div className="flex flex-1 flex-col gap-1.5">
              <span className="block h-1.5 overflow-hidden rounded-[3px] bg-class-white">
                <span className="block h-full bg-class-sky-700" style={{ width: `${Math.round(progress * 100)}%` }} />
              </span>
              <span className="flex justify-between text-class-label font-semibold text-class-ink-soft">
                {audio ? (
                  <>
                    <span>{fmt(time.at)}</span>
                    <span>{fmt(time.total)}</span>
                  </>
                ) : (
                  <span>{t.listen.line(Math.min(lines.length, line + (playing ? 1 : 0)), lines.length)}</span>
                )}
              </span>
            </div>
          </div>
          <div role="group" className="inline-flex self-center rounded-full bg-class-white p-1">
            {[
              [true, t.listen.slow],
              [false, t.listen.normal],
            ].map(([value, label]) => (
              <button
                key={String(value)}
                type="button"
                aria-pressed={slow === value}
                onClick={() => setSlow(value as boolean)}
                className={[
                  'min-h-9 cursor-pointer rounded-full border-0 px-4 font-[inherit] text-class-gloss font-semibold',
                  slow === value ? 'bg-class-field-soft text-class-sky-700' : 'bg-transparent text-class-ink-soft',
                ].join(' ')}
              >
                {label as string}
              </button>
            ))}
          </div>
          {!audio && (
            <p className="m-0 text-center text-class-label text-class-ink-soft">
              <span className="font-semibold text-class-ink">▶ {t.listen.deviceVoice}.</span> {t.listen.deviceVoiceHint}
            </p>
          )}
          {audio && (
            <audio
              ref={audioRef}
              src={`/api/course/media/${audio.id}`}
              preload="metadata"
              onPlay={() => setPlaying(true)}
              onPause={() => setPlaying(false)}
              onEnded={() => setPlaying(false)}
              onLoadedMetadata={(e) => setTime({ at: 0, total: e.currentTarget.duration || audio.durationSec })}
              onTimeUpdate={(e) => setTime({ at: e.currentTarget.currentTime, total: e.currentTarget.duration || audio.durationSec })}
              className="hidden"
            />
          )}
        </section>
      ) : (
        <p className="m-0 rounded-class-md bg-class-field-soft px-4 py-3 text-class-gloss text-class-ink-soft">{t.listen.noVoice}</p>
      )}

      {step.rest.length > 0 && <Blocks blocks={step.rest} glosses={step.glosses} locale={locale} t={t} />}

      {step.scenes.length > 0 && (
        <div className="flex flex-col gap-5">
          <button type="button" onClick={() => setShowText((v) => !v)} className="cl-link self-start" aria-expanded={showText}>
            {showText ? t.listen.hideText : t.listen.showText}
          </button>
          {showText && <Transcript scenes={step.scenes} glosses={step.glosses} locale={locale} t={t} />}
        </div>
      )}
    </div>
  )
}

function Transcript({
  scenes,
  glosses,
  locale,
  t,
}: {
  scenes: DialogueBlock[]
  glosses: Gloss[]
  locale: Locale
  t: ReturnType<typeof usePlayer>['t']
}) {
  const [open, setOpen] = useState<Gloss | null>(null)
  return (
    <div className="flex flex-col gap-6">
      {scenes.map((s, i) => (
        <div key={i} className="flex flex-col gap-2">
          {s.title && <p className="m-0 text-class-label font-semibold text-class-ink-faint">{s.title}</p>}
          <Dialogue block={s} glosses={glosses} onGloss={setOpen} listen={t.words.listen} />
        </div>
      ))}
      {glosses.length > 0 && <GlossList title={t.dialogue.extra} note="" items={glosses} locale={locale} />}
      {open && (
        <p role="status" className="m-0 rounded-class-md bg-class-white px-4 py-3 shadow-[inset_0_0_0_1px_var(--color-class-hairline)]">
          <span lang="pl" className="font-class-display">
            {open.pl}
          </span>{' '}
          <span className="text-class-gloss text-class-ink-soft">— {locale === 'ru' ? open.ru : open.uk}</span>
        </p>
      )}
    </div>
  )
}

/* ── finish ─────────────────────────────────────────────────────────── */

export function FinishStep({
  lesson,
  slug,
  steps,
  onComplete,
}: {
  lesson: LessonDetail
  slug: string
  steps: Step[]
  onComplete: () => void
}) {
  const { t, goTo } = usePlayer()
  const navigate = useNavigate()
  const done = useRef(false)
  useEffect(() => {
    if (!done.current && !lesson.completedAt) {
      done.current = true
      onComplete()
    }
  }, [lesson.completedAt, onComplete])

  const lessonExercises = lesson.exercises.filter((e) => e.sectionKey !== HOMEWORK_KEY && e.autoGradable)
  const rows = lesson.sections
    .filter((s) => s.key !== HOMEWORK_KEY && s.kind !== 'homework')
    .map((s) => {
      const mine = lessonExercises.filter((e) => e.sectionKey === s.key)
      const score = mine.reduce((n, e) => n + (e.attempt && !e.attempt.stale ? e.attempt.score : 0), 0)
      const max = mine.reduce((n, e) => n + (e.attempt && !e.attempt.stale ? e.attempt.max : 0), 0)
      return { key: s.key, label: sectionLabelPl(s), score, max, count: mine.length }
    })
    .filter((r) => r.count > 0)
  const right = rows.reduce((n, r) => n + r.score, 0)
  const total = rows.reduce((n, r) => n + r.max, 0)
  const wrong = lessonExercises.filter((e) => e.attempt && !e.attempt.correct)
  const firstWrong = steps.findIndex((s) => s.kind === 'exercise' && wrong.some((w) => w.id === s.exercise.id))
  const homework = lesson.exercises.filter((e) => e.sectionKey === HOMEWORK_KEY && e.type !== 'unknown')
  const homeworkUrl = `/panel/kurs/${slug}/${lesson.slug}/zadanie`

  usePrimary(
    homework.length > 0
      ? { label: t.finish.startHomework, onClick: () => navigate(homeworkUrl) }
      : lesson.next
        ? { label: `${t.finish.nextLesson(lesson.next.number)} ›`, onClick: () => navigate(`/panel/kurs/${slug}/${lesson.next!.slug}`) }
        : { label: t.finish.backToCourse, onClick: () => navigate(`/panel/kurs/${slug}`) },
  )

  return (
    <div className="flex flex-col gap-7">
      <div className="flex flex-col items-center gap-2 text-center">
        <span className="mb-1 inline-flex h-16 w-16 items-center justify-center rounded-full bg-[#fbf4e2] text-class-heading font-semibold text-class-gold-700">
          ✓
        </span>
        <h1 lang="pl" className="cl-heading m-0">
          {t.finish.great}
        </h1>
        <p className="m-0 text-class-ink-soft">
          {total > 0 ? t.finish.done(lesson.number, right, total) : t.finish.doneNoScore(lesson.number)}
        </p>
      </div>

      {rows.length > 0 && (
        <div className="flex flex-col">
          {rows.map((r) => (
            <div key={r.key} className="flex min-h-12 items-center border-t border-class-line last:border-b">
              <span lang="pl" className="flex-1">
                {r.label}
              </span>
              <span className="text-class-ink-soft">{r.max > 0 ? `${r.score} / ${r.max}` : '—'}</span>
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-col gap-3">
        {homework.length > 0 && (
          <Link
            to={homeworkUrl}
            className="flex items-center gap-3.5 rounded-class-lg bg-class-field p-5 !text-class-ink hover:bg-class-sky-200"
          >
            <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-class-btn bg-class-white text-class-sky-700">
              <Icon name="homework" />
            </span>
            <span className="leading-snug">
              <span lang="pl" className="block font-class-display text-class-subheading">
                {t.finish.homework}
              </span>
              <span className="text-class-gloss text-class-ink-soft">{t.finish.homeworkMeta(homework.length)}</span>
            </span>
          </Link>
        )}
        {wrong.length > 0 && firstWrong >= 0 && (
          <button type="button" onClick={() => goTo(firstWrong)} className="cl-link self-center py-1 text-class-gloss">
            {t.finish.fixWrong(wrong.length)}
          </button>
        )}
      </div>

      {lesson.next && homework.length > 0 && (
        <Link to={`/panel/kurs/${slug}/${lesson.next.slug}`} className="cl-link-quiet self-center">
          {t.finish.nextLesson(lesson.next.number)} ›
        </Link>
      )}
    </div>
  )
}
